import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  submitManualApplication,
  uploadManualApplicationPhoto,
  type ManualApplicationRequest,
} from '../../../api/register';

/** 手机号（中国大陆）校验 */
const PHONE_RE = /^1[3-9]\d{9}$/;
/** 18 位身份证号（末位可为 X） */
const ID_CARD_RE = /^\d{17}[\dXx]$/;
/** 用户名：3-50 位字母数字下划线中划线 */
const USERNAME_RE = /^[\w-]{3,50}$/;

/**
 * 人工开通账号申请表单。
 * 身份证照片先经 POST /manual-application/upload-id-card 上传，
 * 再随申请一起 POST /manual-application 提交（后端转人工审核，无进度查询接口）。
 */
export default function ManualApplicationForm() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState({
    username: '',
    fullName: '',
    phone: '',
    backupPhone: '',
    idCard: '',
    email: '',
    reason: '',
    additionalInfo: '',
  });
  const [idCardPhoto, setIdCardPhoto] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [succeeded, setSucceeded] = useState(false);

  const mutation = useMutation({
    mutationFn: (data: ManualApplicationRequest) => submitManualApplication(data),
    onSuccess: () => setSucceeded(true),
    onError: (err) => setError(err instanceof Error ? err.message : '提交失败，请稍后重试'),
  });

  const set = (key: keyof typeof form) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  /** 选择身份证照片后立即上传（5MB 内 JPG/PNG/WEBP/BMP） */
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setUploading(true);
    try {
      const url = await uploadManualApplicationPhoto(file);
      setIdCardPhoto(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : '照片上传失败');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.fullName.trim()) return setError('请填写姓名');
    if (!PHONE_RE.test(form.phone.trim())) return setError('请输入正确的 11 位手机号');
    if (!ID_CARD_RE.test(form.idCard.trim())) return setError('请输入正确的 18 位身份证号');
    if (!form.email.trim()) return setError('请填写邮箱');
    if (!form.reason.trim()) return setError('请填写申请原因');
    if (form.username.trim() && !USERNAME_RE.test(form.username.trim())) {
      return setError('用户名需为 3-50 位字母、数字、下划线或中划线');
    }
    if (form.backupPhone.trim() && !PHONE_RE.test(form.backupPhone.trim())) {
      return setError('备用手机号格式不正确');
    }
    mutation.mutate({
      username: form.username.trim() || undefined,
      fullName: form.fullName.trim(),
      phone: form.phone.trim(),
      backupPhone: form.backupPhone.trim() || undefined,
      idCard: form.idCard.trim(),
      email: form.email.trim(),
      reason: form.reason.trim(),
      additionalInfo: form.additionalInfo.trim() || undefined,
      idCardPhoto: idCardPhoto || undefined,
    });
  };

  // 提交成功结果态（后端无进度查询接口，提示等待审核即可）
  if (succeeded) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-8 flex flex-col items-center text-center">
        <div className="w-14 h-14 flex items-center justify-center bg-emerald-50 rounded-2xl mb-4">
          <i className="ri-checkbox-circle-line text-emerald-500 text-3xl" />
        </div>
        <p className="text-base font-medium text-gray-800 mb-1">申请已提交</p>
        <p className="text-sm text-gray-400 mb-1">管理员审核通过后，将向您的邮箱发送账号激活邮件</p>
        <p className="text-xs text-gray-300 mb-6">同一身份证号重复提交会被拒绝，请耐心等待审核结果</p>
        <button
          type="button"
          onClick={() => {
            setForm({ username: '', fullName: '', phone: '', backupPhone: '', idCard: '', email: '', reason: '', additionalInfo: '' });
            setIdCardPhoto('');
            setSucceeded(false);
          }}
          className="px-4 py-2.5 text-sm text-teal-600 border border-teal-200 rounded-xl hover:bg-teal-50 cursor-pointer transition-colors"
        >
          再提交一份申请
        </button>
      </div>
    );
  }

  const inputCls =
    'w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-400 transition-colors';

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
      <div>
        <h3 className="text-base font-semibold text-gray-900">人工开通账号申请</h3>
        <p className="text-xs text-gray-400 mt-1">自助注册遇到问题？提交资料由管理员人工为您开通账号</p>
      </div>

      {error && (
        <div className="flex items-center gap-2 px-3 py-2.5 text-sm text-red-600 bg-red-50 rounded-xl">
          <i className="ri-error-warning-line" />
          <span>{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-600 mb-1.5">
            姓名 <span className="text-red-400">*</span>
          </label>
          <input value={form.fullName} onChange={set('fullName')} placeholder="真实姓名" className={inputCls} />
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1.5">用户名（选填）</label>
          <input value={form.username} onChange={set('username')} placeholder="留空默认使用邮箱前缀" className={inputCls} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-600 mb-1.5">
            手机号 <span className="text-red-400">*</span>
          </label>
          <input value={form.phone} onChange={set('phone')} placeholder="11 位手机号" className={inputCls} />
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1.5">备用手机号（选填）</label>
          <input value={form.backupPhone} onChange={set('backupPhone')} placeholder="用于身份找回" className={inputCls} />
        </div>
      </div>

      <div>
        <label className="block text-sm text-gray-600 mb-1.5">
          身份证号 <span className="text-red-400">*</span>
        </label>
        <input value={form.idCard} onChange={set('idCard')} placeholder="18 位身份证号码" className={inputCls} />
      </div>

      <div>
        <label className="block text-sm text-gray-600 mb-1.5">
          邮箱 <span className="text-red-400">*</span>
        </label>
        <input value={form.email} onChange={set('email')} placeholder="用于接收激活邮件" className={inputCls} />
      </div>

      <div>
        <label className="block text-sm text-gray-600 mb-1.5">
          申请原因 <span className="text-red-400">*</span>
        </label>
        <textarea
          value={form.reason}
          onChange={set('reason')}
          rows={3}
          placeholder="例如：手机号已更换无法完成自助注册，申请人工开通"
          className={`${inputCls} resize-none`}
        />
      </div>

      <div>
        <label className="block text-sm text-gray-600 mb-1.5">补充说明（选填）</label>
        <textarea
          value={form.additionalInfo}
          onChange={set('additionalInfo')}
          rows={2}
          placeholder="其他需要说明的情况"
          className={`${inputCls} resize-none`}
        />
      </div>

      {/* 身份证照片 */}
      <div>
        <label className="block text-sm text-gray-600 mb-1.5">身份证照片（选填，仅管理员可见）</label>
        {idCardPhoto ? (
          <div className="flex items-center gap-3 px-3 py-2.5 bg-emerald-50 rounded-xl">
            <i className="ri-checkbox-circle-line text-emerald-500" />
            <span className="text-sm text-emerald-700 flex-1">照片已上传</span>
            <button
              type="button"
              onClick={() => setIdCardPhoto('')}
              className="text-xs text-gray-400 hover:text-red-500 cursor-pointer transition-colors"
            >
              移除
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-full flex flex-col items-center justify-center gap-1.5 py-6 border border-dashed border-gray-200 rounded-xl text-gray-400 hover:border-teal-300 hover:text-teal-500 cursor-pointer transition-colors disabled:opacity-50"
          >
            <i className={uploading ? 'ri-loader-4-line animate-spin text-xl' : 'ri-upload-2-line text-xl'} />
            <span className="text-xs">{uploading ? '上传中...' : '点击上传身份证照片（5MB 内）'}</span>
          </button>
        )}
        <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/bmp" onChange={handleFileChange} className="hidden" />
      </div>

      <button
        type="submit"
        disabled={mutation.isPending || uploading}
        className="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-2.5 bg-teal-500 hover:bg-teal-600 text-white text-sm font-medium rounded-xl cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <i className={mutation.isPending ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'} />
        {mutation.isPending ? '提交中...' : '提交申请'}
      </button>
    </form>
  );
}